import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useModal from '../../../../../hooks/use-close-modal.hook';
import useRequest from '../../../../../hooks/use-request.hook';
import {
  CPDService,
  CPD_DEFAULT_THRESHOLDS,
  validateVoiceSpec
} from '../../../../../services/cpd.service';
import { AlertService } from '../../../../../services/alert.service';
import LogData from '../../../../../components/logs/log-data.component';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Button from '../../../../../components/forms/button.component';
import Title from '../../../../../components/forms/titles.component';
import Input from '../../../../../components/forms/input.component';
import TexArea from '../../../../../components/forms/text-area.component';
import Icon from '../../../../../components/forms/icon.component';
import Select from 'react-select';

/**
 * Modal de plantillas CPD. El body va contra `upsertTemplateSchema` del backend
 * (`config` = `fullConfigSchema`, `.strict()`), asi que:
 *  - `thresholds` viaja siempre con los 4 numericos; `dtmfHumanDigits` y
 *    `mlConfidenceMin` solo si tienen valor.
 *  - los strings opcionales (`transferTo`, `toneSpec`, `toneCarrier`,
 *    `voiceSpec`) se OMITEN cuando estan vacios: el schema pide `min(1)` y un
 *    '' los tumbaria con 400.
 *  - `mode=tone` exige `toneSpec` o `toneCarrier`; `mode=voice` exige
 *    `voiceSpec`; `action=transfer` exige `transferTo`.
 */

interface AddTemplateProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

const MODE_OPTIONS = [
  { value: 'energy', label: 'Energy (early media)' },
  { value: 'tone', label: 'Tone Detect (beep del buzon)' },
  { value: 'dtmf', label: 'DTMF' },
  { value: 'ml', label: 'ML' },
  { value: 'voice', label: 'Voice / AMD (post-answer)' }
];

const ACTION_OPTIONS = [
  { value: 'hangup_603', label: 'Colgar con 603 Decline' },
  { value: 'continue', label: 'Continuar la llamada' },
  { value: 'transfer', label: 'Transferir' }
];

const CARRIER_OPTIONS = [
  { value: '', label: '— Ninguno —' },
  { value: 'TELCEL', label: 'TELCEL' },
  { value: 'MOVISTAR', label: 'MOVISTAR' },
  { value: 'ATT', label: 'AT&T' },
  { value: 'ALTAN', label: 'ALTAN REDES' }
];

const TONE_SPEC_REGEX = /^[A-Za-z0-9_,.\-+=\s]{1,255}$/;

const trimmed = (v: unknown): string => String(v ?? '').trim();

export default function AddTemplateModal({ show, onRequestClose, payload }: AddTemplateProps) {
  const { t } = useTranslation();
  const { call } = useRequest();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const defaultValues = {
    id: '',
    name: '',
    description: '',
    config: {
      mode: 'energy',
      action: 'hangup_603',
      transferTo: '',
      toneSpec: '',
      toneCarrier: '',
      toneStartDelayMs: '',
      voiceSpec: '',
      bypass: false,
      thresholds: {
        maxAnalysisMs: String(CPD_DEFAULT_THRESHOLDS.maxAnalysisMs),
        minEnergyDbfs: String(CPD_DEFAULT_THRESHOLDS.minEnergyDbfs),
        minVoiceMs: String(CPD_DEFAULT_THRESHOLDS.minVoiceMs),
        maxSilenceMs: String(CPD_DEFAULT_THRESHOLDS.maxSilenceMs),
        dtmfHumanDigits: '',
        mlConfidenceMin: ''
      }
    }
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const currentMode = watch('config.mode');
  const currentAction = watch('config.action');
  const currentCarrier = watch('config.toneCarrier');

  useEffect(() => {
    if (!payload) {
      reset(defaultValues);
      return;
    }

    const cfg = payload.config ?? {};
    const th = cfg.thresholds ?? {};

    reset({
      id: payload.id || payload._id || '',
      name: payload.name || '',
      description: payload.description || '',
      config: {
        mode: cfg.mode || 'energy',
        action: cfg.action || 'hangup_603',
        transferTo: cfg.transferTo ?? '',
        toneSpec: cfg.toneSpec ?? '',
        toneCarrier: cfg.toneCarrier ?? '',
        toneStartDelayMs: String(cfg.toneStartDelayMs ?? ''),
        voiceSpec: cfg.voiceSpec ?? '',
        bypass: cfg.bypass ?? false,
        thresholds: {
          maxAnalysisMs: String(th.maxAnalysisMs ?? CPD_DEFAULT_THRESHOLDS.maxAnalysisMs),
          minEnergyDbfs: String(th.minEnergyDbfs ?? CPD_DEFAULT_THRESHOLDS.minEnergyDbfs),
          minVoiceMs: String(th.minVoiceMs ?? CPD_DEFAULT_THRESHOLDS.minVoiceMs),
          maxSilenceMs: String(th.maxSilenceMs ?? CPD_DEFAULT_THRESHOLDS.maxSilenceMs),
          dtmfHumanDigits: th.dtmfHumanDigits ?? '',
          mlConfidenceMin: th.mlConfidenceMin === undefined ? '' : String(th.mlConfidenceMin)
        }
      }
    });
  }, [payload, show]);

  const cleanBootstrapBackdrop = () => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const closeModal = () => {
    onRequestClose(false);
    reset(defaultValues);
    cleanBootstrapBackdrop();
  };

  /** form → body de `upsertTemplateSchema`. Omite todo string opcional vacio. */
  const buildBody = (formData: any) => {
    const f = formData.config;

    const thresholds: Record<string, unknown> = {
      maxAnalysisMs: Number(f.thresholds.maxAnalysisMs),
      minEnergyDbfs: Number(f.thresholds.minEnergyDbfs),
      minVoiceMs: Number(f.thresholds.minVoiceMs),
      maxSilenceMs: Number(f.thresholds.maxSilenceMs)
    };
    if (trimmed(f.thresholds.dtmfHumanDigits) !== '') {
      thresholds.dtmfHumanDigits = trimmed(f.thresholds.dtmfHumanDigits);
    }
    if (trimmed(f.thresholds.mlConfidenceMin) !== '') {
      thresholds.mlConfidenceMin = Number(f.thresholds.mlConfidenceMin);
    }

    const config: Record<string, unknown> = {
      mode: f.mode,
      action: f.action,
      bypass: Boolean(f.bypass),
      thresholds
    };
    if (trimmed(f.transferTo)) config.transferTo = trimmed(f.transferTo);
    if (trimmed(f.toneSpec)) config.toneSpec = trimmed(f.toneSpec);
    if (trimmed(f.toneCarrier)) config.toneCarrier = trimmed(f.toneCarrier);
    if (trimmed(f.toneStartDelayMs) !== '') {
      config.toneStartDelayMs = Number(f.toneStartDelayMs);
    }
    if (trimmed(f.voiceSpec)) config.voiceSpec = trimmed(f.voiceSpec);

    const body: Record<string, unknown> = { name: trimmed(formData.name), config };
    if (trimmed(formData.description)) body.description = trimmed(formData.description);
    return body;
  };

  const submit = async (formData: any, buttonId: string) => {
    try {
      const isConfirmed = await AlertService.warning(
        t('general.atention'),
        payload != null
          ? `Estás por actualizar la plantilla "${payload?.name || formData.name}".`
          : `Estás por registrar una nueva plantilla CPD.`,
        () => Promise.resolve(true)
      );

      if (!isConfirmed) return;

      const templateId = payload?.id || payload?._id || formData.id;
      const submissionBody = buildBody(formData);

      if (payload != null) {
        await call(() => CPDService.updateTemplates(String(templateId), submissionBody));
        AlertService.toastSuccess('Plantilla actualizada exitosamente');
      } else {
        await call(() => CPDService.saveTemplates(submissionBody));
        AlertService.toastSuccess('Plantilla registrada exitosamente');
      }

      onRequestClose(true);
      reset(defaultValues);
      cleanBootstrapBackdrop();
      LogData(submissionBody, buttonId, window.location.href);
    } catch (error: any) {
      console.error(error);
      AlertService.toastError(error.error || 'Ocurrió un error al procesar la solicitud');
    }
  };

  const renderSelect = (
    name: 'config.mode' | 'config.action' | 'config.toneCarrier',
    options: { value: string; label: string }[],
    label: string,
    required: boolean
  ) => (
    <div className="fv-row mb-5">
      <label className={required ? 'form-label required' : 'form-label'}>{label}</label>
      <Controller
        control={control}
        name={name}
        rules={required ? { required: true } : {}}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select
              options={options}
              isClearable={false}
              className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
              classNamePrefix="form-select-custom"
              onChange={(opt: any) => onChange(opt ? opt.value : options[0].value)}
              value={options.find((opt) => opt.value === (value ?? '')) ?? options[0]}
            />
            <InvalidFeedback error={error} />
          </>
        )}
      />
    </div>
  );

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-1000px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={closeModal}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>

          <form
            id="cpdTemplateForm"
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = payload ? 'editTemplate-9384729384' : 'addTemplate-10293847';
              handleSubmit((data) => submit(data, buttonID))(e);
            }}
          >
            <div className="modal-body pt-0 pb-12 px-6 px-xl-15">
              <div className="text-center mb-8">
                <h1 className="mb-2 fs-2x">
                  {payload ? 'Editar Plantilla CPD' : 'Agregar Nueva Plantilla CPD'}
                </h1>
                <p className="text-muted fw-semibold fs-6">
                  La plantilla es una política CPD completa; operadores y rutas solo la ajustan con
                  overrides.
                </p>
                <div className="separator border-2 my-4"></div>
              </div>

              <div className="row g-7">
                <div className="col-lg-6">
                  <Title
                    title="Datos generales"
                    subtitle="Información principal"
                    icon="ki-notepad-edit"
                  />

                  <div className="fv-row mb-5">
                    <Input
                      label="Nombre del Template"
                      requiredIndicator="required"
                      placeholder="Ej. Detección Telcel Agresiva"
                      errors={errors.name}
                      {...register('name', {
                        required: 'El nombre es requerido',
                        maxLength: { value: 80, message: 'Máximo 80 caracteres' },
                        setValueAs: (v) => String(v ?? '').trim()
                      })}
                    />
                  </div>

                  <div className="fv-row mb-5">
                    <label className="form-label">Descripción</label>
                    <TexArea
                      rows={3}
                      placeholder="Escribe el propósito de este template..."
                      {...register('description', {
                        maxLength: { value: 255, message: 'Máximo 255 caracteres' }
                      })}
                    />
                    <InvalidFeedback error={errors.description} />
                  </div>

                  {renderSelect('config.mode', MODE_OPTIONS, 'Modo', true)}
                  {renderSelect('config.action', ACTION_OPTIONS, 'Acción al detectar buzón', true)}

                  {currentAction === 'transfer' && (
                    <div className="fv-row mb-5">
                      <Input
                        label="Transferir a"
                        requiredIndicator="required"
                        placeholder="Extensión o número"
                        errors={errors.config?.transferTo}
                        {...register('config.transferTo', {
                          required: 'action=transfer requiere transferTo',
                          maxLength: { value: 64, message: 'Máximo 64 caracteres' }
                        })}
                      />
                    </div>
                  )}

                  <div className="mb-5 d-flex flex-column justify-content-center align-items-start">
                    <label className="form-check form-switch form-check-custom form-check-solid gap-3 style-pointer">
                      <input
                        className="form-check-input w-45px h-25px"
                        type="checkbox"
                        {...register('config.bypass')}
                      />
                      <span className="form-check-label fw-bold text-gray-700 fs-7">
                        Activar Bypass (la llamada no pasa por CPD)
                      </span>
                    </label>
                  </div>

                  {currentMode === 'tone' && (
                    <>
                      {renderSelect(
                        'config.toneCarrier',
                        CARRIER_OPTIONS,
                        'Operador del tono (toneCarrier)',
                        false
                      )}

                      <div className="fv-row mb-5">
                        <Input
                          label="Especificación de Tono (toneSpec)"
                          requiredIndicator={trimmed(currentCarrier) ? '' : 'required'}
                          placeholder="vmtelcel 404 r +1000 hangup 21 1"
                          errors={errors.config?.toneSpec}
                          {...register('config.toneSpec', {
                            validate: (v: string) => {
                              const val = trimmed(v);
                              if (val === '') {
                                return (
                                  trimmed(currentCarrier) !== '' ||
                                  'mode=tone requiere toneSpec o toneCarrier (al menos uno)'
                                );
                              }
                              return (
                                TONE_SPEC_REGEX.test(val) ||
                                'Solo letras, números, espacios y _ , . - + ='
                              );
                            }
                          })}
                        />
                      </div>

                      <div className="fv-row mb-5">
                        <Input
                          label="Delay antes de tone_detect (ms)"
                          type="number"
                          placeholder="Ej. 25000 (MOVISTAR)"
                          errors={errors.config?.toneStartDelayMs}
                          {...register('config.toneStartDelayMs', {
                            validate: (v: string) => {
                              if (trimmed(v) === '') return true;
                              const n = Number(v);
                              if (!Number.isInteger(n)) return 'Debe ser un entero';
                              return (n >= 0 && n <= 300000) || 'Debe estar entre 0 y 300000';
                            }
                          })}
                        />
                      </div>
                    </>
                  )}

                  {currentMode === 'voice' && (
                    <div className="fv-row mb-5">
                      <Input
                        label="VoiceSpec (parámetros de mod_amd)"
                        requiredIndicator="required"
                        placeholder="total_analysis_time=6000,after_greeting_silence=800"
                        errors={errors.config?.voiceSpec}
                        {...register('config.voiceSpec', {
                          validate: (v: string) => {
                            const val = trimmed(v);
                            if (val === '') {
                              return 'mode=voice requiere voiceSpec (sin él, un nodo sin amd.conf.xml corre mod_amd con umbrales en cero)';
                            }
                            return validateVoiceSpec(val) ?? true;
                          }
                        })}
                      />
                      <div className="form-text">
                        Pares <code>clave=entero</code> separados por comas, con claves de mod_amd.
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-lg-6 bg-light-nested rounded-4 p-6 border border-dashed border-gray-300">
                  <Title
                    title="Parámetros dinámicos (thresholds)"
                    subtitle={
                      currentMode === 'energy'
                        ? 'Se usan para decidir humano vs. buzón.'
                        : 'El backend los exige siempre; en este modo no deciden nada.'
                    }
                    icon="ki-setting-2"
                    type={currentMode === 'energy' ? 'primary' : 'info'}
                  />

                  {currentMode !== 'energy' && (
                    <div className="alert bg-light-info border border-info d-flex flex-column p-5 rounded mb-5">
                      <span className="fw-bold fs-7 text-info mb-1">
                        Modo {MODE_OPTIONS.find((o) => o.value === currentMode)?.label}
                      </span>
                      <p className="fs-8 text-gray-700 m-0">
                        La política completa lleva thresholds de todas formas. Se guardan tal cual y
                        vuelven a aplicar si algún día cambias el modo a Energy.
                      </p>
                    </div>
                  )}

                  <div className="row g-4">
                    <div className="col-sm-6">
                      <Input
                        label="Max Analysis Ms (500 – 60000)"
                        type="number"
                        placeholder="Ej. 8000"
                        errors={errors.config?.thresholds?.maxAnalysisMs}
                        {...register('config.thresholds.maxAnalysisMs', {
                          required: 'Requerido',
                          min: { value: 500, message: 'Mínimo 500' },
                          max: { value: 60000, message: 'Máximo 60000' }
                        })}
                      />
                    </div>

                    <div className="col-sm-6">
                      <Input
                        label="Min Energy Dbfs (-80 – 0)"
                        type="number"
                        placeholder="Ej. -40"
                        errors={errors.config?.thresholds?.minEnergyDbfs}
                        {...register('config.thresholds.minEnergyDbfs', {
                          required: 'Requerido',
                          min: { value: -80, message: 'Mínimo -80' },
                          max: { value: 0, message: 'Máximo 0' }
                        })}
                      />
                    </div>

                    <div className="col-sm-6">
                      <Input
                        label="Min Voice Ms (100 – 20000)"
                        type="number"
                        placeholder="Ej. 1500"
                        errors={errors.config?.thresholds?.minVoiceMs}
                        {...register('config.thresholds.minVoiceMs', {
                          required: 'Requerido',
                          min: { value: 100, message: 'Mínimo 100' },
                          max: { value: 20000, message: 'Máximo 20000' }
                        })}
                      />
                    </div>

                    <div className="col-sm-6">
                      <Input
                        label="Max Silence Ms (100 – 20000)"
                        type="number"
                        placeholder="Ej. 1200"
                        errors={errors.config?.thresholds?.maxSilenceMs}
                        {...register('config.thresholds.maxSilenceMs', {
                          required: 'Requerido',
                          min: { value: 100, message: 'Mínimo 100' },
                          max: { value: 20000, message: 'Máximo 20000' }
                        })}
                      />
                    </div>

                    {currentMode === 'dtmf' && (
                      <div className="col-sm-12">
                        <Input
                          label="DTMF dígitos humano (opcional)"
                          placeholder="0-9 * # (máx. 8)"
                          errors={errors.config?.thresholds?.dtmfHumanDigits}
                          {...register('config.thresholds.dtmfHumanDigits', {
                            pattern: {
                              value: /^[0-9*#]{0,8}$/,
                              message: '0..8 caracteres de 0-9, *, #'
                            }
                          })}
                        />
                      </div>
                    )}

                    {currentMode === 'ml' && (
                      <div className="col-sm-12">
                        <Input
                          label="ML Confidence Min (0 – 1, opcional)"
                          type="number"
                          step="any"
                          placeholder="Ej. 0.8"
                          errors={errors.config?.thresholds?.mlConfidenceMin}
                          {...register('config.thresholds.mlConfidenceMin', {
                            validate: (v: string) => {
                              if (trimmed(v) === '') return true;
                              const n = Number(v);
                              return (n >= 0 && n <= 1) || 'Debe estar entre 0 y 1';
                            }
                          })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-center align-items-center pt-10 gap-3">
                <button
                  type="button"
                  className="btn btn-light hover-scale fs-7 fw-bold"
                  onClick={closeModal}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  id="sendCpdTemplate"
                  className="btn btn-primary hover-scale fs-7 fw-bold"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  Guardar Cambios
                  <Icon icon="ki-send" size="fs-4" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
